import { DEFAULT_CURRENCY, isCurrencyCode, type CurrencyCode } from '@tk/shared';
import { useSyncExternalStore } from 'react';

/**
 * Järjestelmän valuutta käyttöliittymässä.
 *
 * Toisin kuin teema ja kieli, valuutta **ei** ole selaimen asetus: se tulee
 * palvelimelta (`/api/me` → `settings.currency`) ja on sama kaikille.
 * Siksi sitä ei tallenneta localStorageen — vanhentunut kopio näyttäisi
 * kruunuja sen jälkeen, kun ylläpitäjä on vaihtanut asetuksen takaisin
 * euroihin, ja luvut olisivat hiljaa väärässä valuutassa.
 *
 * Miksi erillinen tila eikä pelkkä istuntokonteksti: rahan muotoilu
 * (`useKaannos().m.raha`) on samassa koukussa kuin käännökset, ja sitä
 * kutsutaan myös siellä, missä istuntokontekstia ei ole (kirjautumissivu,
 * virhenäkymät). Arvo asetetaan kerran istunnon latauksen yhteydessä
 * ennen ensimmäistä piirtoa, joten näkymät eivät koskaan näytä väärää
 * valuuttaa hetkeksikään.
 */
let nykyinen: CurrencyCode = DEFAULT_CURRENCY;
const kuuntelijat = new Set<() => void>();

export function asetaValuutta(valuutta: unknown): void {
  // Palvelin lupaa tuetun koodin, mutta vanha selain voi olla auki uutta
  // palvelinta vasten. Tuntematon koodi kaataisi `Intl`in jokaisessa
  // rahakentässä, joten oletus on parempi kuin tyhjä näkymä.
  const seuraava = isCurrencyCode(valuutta) ? valuutta : DEFAULT_CURRENCY;
  if (seuraava === nykyinen) return;
  nykyinen = seuraava;
  for (const kuuntelija of kuuntelijat) kuuntelija();
}

function tilaa(kuuntelija: () => void): () => void {
  kuuntelijat.add(kuuntelija);
  return () => {
    kuuntelijat.delete(kuuntelija);
  };
}

/** Voimassa oleva valuutta. Kaikki rahaa näyttävä koodi kulkee tämän kautta. */
export function useValuutta(): CurrencyCode {
  return useSyncExternalStore(tilaa, () => nykyinen);
}

/** Valuutta ilman koukkua — vain sinne, missä React ei ole mukana. */
export function valuuttaNyt(): CurrencyCode {
  return nykyinen;
}
